import { Inject, Injectable, Optional } from '@angular/core';
import { Scripts } from '@core/data-access/models/script-injector.model';
import { ScriptsAPI } from '@core/data-access/services/scripts/scripts-api';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EXHIBITION_DEBOUNCE_TIME, WINDOW } from '@shared/tokens';
import { catchError, debounceTime, EMPTY, map, of, switchMap, tap } from 'rxjs';
import { QuestionnaireDataInput } from '../customization/customization.interfaces';
import {
  ActionTypes,
  AddAllExhibitionsToListAction,
  GetAllDepartments,
  GetAllOccupationalGroups,
  GetAllProfessions,
  GetAllTitles,
  GetDepartments,
  GetExhibitionHistoryList,
  GetListOfAllExhibitions,
  GetOccupationalGroups,
  GetProfessions,
  GetScripts,
  GetTitles,
  ReloadScripts,
  ResetScripts,
  SetAllDepartments,
  SetAllOccupationalGroups,
  SetAllProfessions,
  SetAllTitles,
  SetDepartments,
  SetExhibitionHistoryList,
  SetOccupationalGroups,
  SetProfessions,
  SetScripts,
  SetTitles
} from './exhibition.actions';
import { ExhibitionHistoryListModel, ExhibitionModel, SelectOption } from './exhibition.interface';
import { ExhibitionService } from './exhibition.service';

@Injectable()
export class ExhibitionEffect {
  loadAllExhibitionsList$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetListOfAllExhibitions>(ActionTypes.GET_LIST_OF_ALL_EXHIBITIONS),
      debounceTime(this.exhibitionDebounceTime),
      switchMap((data: any) => {
        const langCode = data.payload;

        return this.exhibitionService.getAllExhibitionsList(langCode).pipe(
          map((allExhibitionsList: ExhibitionModel[]) => {
            const ListOfExhibitions: ExhibitionModel[] = allExhibitionsList;

            function getUTCdate(date) {
              return new Date(
                date.getUTCFullYear(),
                date.getUTCMonth(),
                date.getUTCDate(),
                date.getUTCHours(),
                date.getUTCMinutes(),
                date.getUTCSeconds()
              );
            }

            // TODO remove once coming from clicks with time
            ListOfExhibitions.forEach((exhibition) => {
              let endDate = <string>exhibition.endDate;

              if (navigator.userAgent.indexOf('MSIE') !== -1 || navigator.appVersion.indexOf('Trident/') > 0) {
                endDate = endDate.replace('00:00:00', '11:59:59'); // "Z" stands for UTC
                const endDateObj = new Date(endDate);
                exhibition.endDate = endDateObj;
              } else {
                endDate = endDate.replace('00:00:00', '11:59:59Z'); // "Z" stands for UTC
                const endDateObj = new Date(endDate);
                exhibition.endDate = endDateObj;
              }

              let startDate = <string>exhibition.startDate;

              if (navigator.userAgent.indexOf('MSIE') !== -1 || navigator.appVersion.indexOf('Trident/') > 0) {
                startDate = startDate.replace('00:00:00', '12:00:00');

                const startDateObj = new Date(startDate);
                //console.log(startDateObj);
                exhibition.startDate = startDateObj;
              } else {
                startDate = startDate.replace('00:00:00', '12:00:00Z');

                const startDateObj = new Date(startDate);
                exhibition.startDate = startDateObj;
              }
            });

            return new AddAllExhibitionsToListAction(ListOfExhibitions);
          }),
          catchError((error) => {
            console.log(error);
            return of(new AddAllExhibitionsToListAction([]));
          })
        );
      })
    )
  );

  loadExhibitionHistoryList$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetExhibitionHistoryList>(ActionTypes.GET_EXHIBITION_HISTORY_LIST),
      debounceTime(this.exhibitionDebounceTime),
      switchMap((data: any) => {
        const userId = data.payload;
        if (!userId) {
          return EMPTY;
        }

        return this.exhibitionService.getExhibitionHistoryList().pipe(
          map(
            (exhibitionHistoryList: ExhibitionHistoryListModel[]) => new SetExhibitionHistoryList(exhibitionHistoryList)
          ),
          catchError((error) => {
            console.log(error);
            return of(new SetExhibitionHistoryList([]));
          })
        );
      })
    )
  );

  getScripts$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetScripts>(ActionTypes.GET_SCRIPTS),
      debounceTime(this.exhibitionDebounceTime),
      switchMap((data: any) => {
        const eventId = data.payload;
        if (eventId === null) {
          return EMPTY;
        }

        return this.scriptsAPI.getEventScripts$(eventId).pipe(
          map((scripts: Scripts) => {
            // transform JSON result to object with keys as ticketGroup Ids and values as ticketSales
            return new SetScripts(scripts);
          }),
          catchError((error) => {
            console.log(error);
            return of(new SetScripts(null));
          })
        );
      })
    )
  );

  reloadScripts$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<ReloadScripts>(ActionTypes.RELOAD_SCRIPTS),
        tap(({ payload }) => {
          this.window.location.href = payload;
        })
      ),
    { dispatch: false }
  );

  resetScripts$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ResetScripts>(ActionTypes.RESET_SCRIPTS),
      map(() => new ReloadScripts(''))
    )
  );

  getProfessions$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetProfessions>(ActionTypes.GET_PROFESSIONS),
      debounceTime(this.exhibitionDebounceTime),
      switchMap((eventId) => {
        if (eventId === null) {
          return EMPTY;
        }

        return this.exhibitionService.getProfessions(eventId.payload).pipe(
          map((selectOptions: QuestionnaireDataInput[]) => {
            const professionsOptions: SelectOption[] = selectOptions
              .slice(0)
              .sort((a, b) => a.order - b.order)
              .map((option): SelectOption => {
                return {
                  key: option.id,
                  value: option.text,
                  translate: true
                };
              });
            return new SetProfessions(professionsOptions);
          }),
          catchError((error) => {
            console.log(error);
            return of(new SetProfessions(null));
          })
        );
      })
    )
  );

  getDepartments$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetDepartments>(ActionTypes.GET_DEPARTMENTS),
      debounceTime(this.exhibitionDebounceTime),
      switchMap((eventId) => {
        if (eventId === null) {
          return EMPTY;
        }

        return this.exhibitionService.getDepartments(eventId.payload).pipe(
          map((selectOptions: QuestionnaireDataInput[]) => {
            const departmentOptions: SelectOption[] = selectOptions
              .slice(0)
              .sort((a, b) => a.order - b.order)
              .map((option): SelectOption => {
                return {
                  key: option.id,
                  value: option.text,
                  translate: true
                };
              });
            return new SetDepartments(departmentOptions);
          }),
          catchError((error) => {
            console.log(error);
            return of(new SetDepartments(null));
          })
        );
      })
    )
  );

  getOccupationalGroups$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetOccupationalGroups>(ActionTypes.GET_OCCUPATIONAL_GROUPS),
      debounceTime(this.exhibitionDebounceTime),
      switchMap((eventId) => {
        if (eventId === null) {
          return EMPTY;
        }

        return this.exhibitionService.getOccupationalGroups(eventId.payload).pipe(
          map((selectOptions: QuestionnaireDataInput[]) => {
            const occupationalGroupsOptions: SelectOption[] = selectOptions
              .slice(0)
              .sort((a, b) => a.order - b.order)
              .map((option): SelectOption => {
                return {
                  key: option.id,
                  value: option.text,
                  translate: true
                };
              });
            return new SetOccupationalGroups(occupationalGroupsOptions);
          }),
          catchError((error) => {
            console.log(error);
            return of(new SetOccupationalGroups(null));
          })
        );
      })
    )
  );

  getTitles$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetTitles>(ActionTypes.GET_TITLES),
      debounceTime(this.exhibitionDebounceTime),
      switchMap((eventId) => {
        if (eventId === null) {
          return EMPTY;
        }

        return this.exhibitionService.getTitles(eventId.payload).pipe(
          map((selectOptions: QuestionnaireDataInput[]) => {
            const titleOptions: SelectOption[] = selectOptions
              .slice(0)
              .sort((a, b) => a.order - b.order)
              .map((option): SelectOption => {
                return {
                  key: option.id,
                  value: option.text,
                  translate: true
                };
              });

            return new SetTitles(titleOptions);
          }),
          catchError((error) => {
            console.log(error);
            return of(new SetTitles(null));
          })
        );
      })
    )
  );

  getAllProfessions$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetAllProfessions>(ActionTypes.GET_ALL_PROFESSIONS),
      debounceTime(this.exhibitionDebounceTime),
      switchMap(() => {
        return this.exhibitionService.getAllProfessions().pipe(
          map((selectOptions: QuestionnaireDataInput[]) => {
            const professionsOptions = selectOptions.slice(0).sort((a, b) => a.order - b.order);

            // transform JSON result to object with keys as ticketGroup Ids and values as ticketSales
            return new SetAllProfessions(professionsOptions);
          }),
          catchError((error) => {
            console.log(error);
            return of(new SetAllProfessions(null));
          })
        );
      })
    )
  );

  getAllDepartments$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetAllDepartments>(ActionTypes.GET_ALL_DEPARTMENTS),
      debounceTime(this.exhibitionDebounceTime),
      switchMap(() => {
        return this.exhibitionService.getAllDepartments().pipe(
          map((selectOptions: QuestionnaireDataInput[]) => {
            const departmentsOptions = selectOptions.slice(0).sort((a, b) => a.order - b.order);

            // transform JSON result to object with keys as ticketGroup Ids and values as ticketSales
            return new SetAllDepartments(departmentsOptions);
          }),
          catchError((error) => {
            console.log(error);
            return of(new SetAllDepartments(null));
          })
        );
      })
    )
  );

  getAllOccupationalGroups$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetAllOccupationalGroups>(ActionTypes.GET_ALL_OCCUPATIONAL_GROUPS),
      debounceTime(this.exhibitionDebounceTime),
      switchMap(() => {
        return this.exhibitionService.getAllOccupationalGroups().pipe(
          map((selectOptions: QuestionnaireDataInput[]) => {
            const occupationalGroupsOptions = selectOptions.slice(0).sort((a, b) => a.order - b.order);

            // transform JSON result to object with keys as ticketGroup Ids and values as ticketSales
            return new SetAllOccupationalGroups(occupationalGroupsOptions);
          }),
          catchError((error) => {
            console.log(error);
            return of(new SetAllOccupationalGroups(null));
          })
        );
      })
    )
  );

  getAllTitles$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetAllTitles>(ActionTypes.GET_ALL_TITLES),
      debounceTime(this.exhibitionDebounceTime),
      switchMap(() => {
        return this.exhibitionService.getAllTitles().pipe(
          map((selectOptions: QuestionnaireDataInput[]) => {
            const allTitlesOptions = selectOptions.slice(0).sort((a, b) => a.order - b.order);

            return new SetAllTitles(allTitlesOptions);
          }),
          catchError((error) => {
            console.log(error);
            return of(new SetAllTitles(null));
          })
        );
      })
    )
  );

  constructor(
    @Optional() @Inject(WINDOW) private window: Window,
    @Optional() @Inject(EXHIBITION_DEBOUNCE_TIME) private exhibitionDebounceTime: number,
    private actions$: Actions,
    private exhibitionService: ExhibitionService,
    private scriptsAPI: ScriptsAPI
  ) {}
}
